import * as THREE from 'three';
import { DRUID_PATH } from '@somnhold/content';
import { counterLayout, hexToWorld, kilnLayout, pineLayout, type BattleState, type CounterLayout, type KilnLayout, type MapDef, type PineLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, Precip, type OmenCtx } from '../omens/common.ts';
import { std } from '../materials.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Slope of the kilns (docs/sendas/druida.md, chapter 2, summer), for the druid's path: tall dry grass, hot pale
 * stone, the charcoal kilns smoking in a row, a hard orange sun through the smoke (a bright scene: summer noon). Each
 * place:
 *
 * - `kilnSlope` (act 1 and the opening): Hob's slope, the six kilns (domes of dark earth, a stone mouth with Olmo's
 *   face carved on it, a column of smoke each): alight they burn on top, cracking they glow red through the earth with
 *   white smoke and the burst's ring, burst a crater, covered a cap of fresh earth; the outcrops; the old pine up the
 *   slope, Los Hornos down in the valley.
 * - `oldPine` (act 2 and Espino's scene): the old pine on its rock at the top with its great closed cone and the second
 *   face, the strip's trees (standing, then a log and a stump), the great fire coming up from the valley as a line of
 *   flames with the black ground behind it; an ember's white smoke where it will fall, its fire after.
 * - `counterSlope` (act 3, the test): the slope in patches of grass (each its own tone, so the patches read), the pine
 *   on its band of bare stone across the middle; a patch alight burns with flames over its cells, black after; the
 *   great fire's patches brighter; the smoke columns lean the way the wind blows (and bend before it turns); the old
 *   wyvern's dive ring and its breath's line; the pine's scorches; the cone opening.
 * - `kilnCine` (the cinematic), `pineFace` (the close: the cone open, the burnt slope, the kilns safe and the lights of
 *   Los Hornos at dusk), `nightRock` (the campfire on a flat stone over the burning valley).
 *
 * Draw calls: the dressing (one merged mesh), the kilns (three instanced), the strip's trees (two instanced), the
 * grass of the patches (one instanced), the flames (one instanced), the smoke (one instanced, its own shader), the
 * scorch sheet (one), the rings (one), the breath's line (one), the pine's cone (two), the ash (one), the glow layer,
 * the sky dome. No lights of its own: the board's are repainted (HOURS).
 */

export const HORNOS_PLACES = new Set(['kilnSlope', 'oldPine', 'counterSlope', 'kilnCine', 'pineFace', 'nightRock']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const FIRE = new THREE.Color('#ff6a24');
const HOT = new THREE.Color('#ffb44a');
const PALE = new THREE.Color('#f2f4f6');
const GRASS = ['#a6994e', '#968e46', '#b2a256', '#8a8a44', '#7e8442'];
const GREEN_GRASS = ['#8c9442', '#7e8a3c', '#9aa04a'];
const STONE = ['#88837b', '#7a756e', '#948e85'];
const EARTH = ['#4c3b2e', '#3e3026', '#5a4636'];
const BARK = ['#6a4630', '#5a3a28', '#7a5238'];
const PINE = ['#2f4a32', '#3a5a3a', '#28402c'];
const CHAR = '#1d1a19';

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/** The light by the hour: a hard summer sun through smoke (warm, bright), dusk with the fire below, night by the fire. */
const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string] }> = {
  day: { bg: '#9fa3a6', fog: ['#a9a49c', 30, 120], hemi: ['#f4ecdf', '#3a3026', 2.15], key: ['#ffd8a8', 2.7], rim: ['#a8c4e8', 1.2], sky: ['#5c7ea6', '#bdb4a8', '#ffd6a0'] },
  dawn: { bg: '#a39a98', fog: ['#aa9e96', 28, 110], hemi: ['#ffe8dc', '#3a2e28', 2.3], key: ['#ffc8a0', 2.6], rim: ['#b0c0ee', 1.3], sky: ['#5a6a8e', '#c0a8a0', '#ffd0a8'] },
  dusk: { bg: '#5e4640', fog: ['#6c5048', 24, 100], hemi: ['#ffd0aa', '#2a1c18', 2.2], key: ['#ffa060', 2.5], rim: ['#7a8ad0', 1.5], sky: ['#26233e', '#9c5a4c', '#ffb070'] },
  night: { bg: '#0d0a0c', fog: ['#1c1112', 18, 70], hemi: ['#9088a0', '#100808', 1.3], key: ['#b8acd8', 0.8], rim: ['#d0603a', 1.4], sky: ['#04040a', '#2c1410', '#c44a2a'] },
};

/**
 * Smoke columns (instanced open cylinders, `aMix`: seed, strength, grey): rolling noise going up, widening and
 * fading with height, leaning with the wind (`uLean`, world x/z a unit of height).
 */
const smokeMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { uTime: { value: 0 }, uLean: { value: new THREE.Vector2(0, 0) } },
    vertexShader: /* glsl */ `
      attribute vec3 aMix; uniform float uTime; uniform vec2 uLean;
      varying vec2 vUv; varying vec3 vMix;
      void main() {
        vUv = uv; vMix = aMix;
        float h = uv.y;
        vec3 p = position; p.xz *= 1.0 + h * 3.2;
        vec4 w = modelMatrix * instanceMatrix * vec4(p, 1.0);
        float top = (modelMatrix * instanceMatrix * vec4(0.0, 1.0, 0.0, 1.0)).y - (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).y;
        float up = h * top;
        w.xz += uLean * up * h * 0.9 + vec2(sin(uTime * 0.7 + aMix.x * 6.0 + h * 3.0), cos(uTime * 0.5 + aMix.x * 4.0)) * h * 0.25;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; varying vec2 vUv; varying vec3 vMix;
      float hh(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(hh(i), hh(i + vec2(1, 0)), u.x), mix(hh(i + vec2(0, 1)), hh(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float h = vUv.y;
        float a = n(vec2(vUv.x * 6.0 + vMix.x * 9.0, h * 4.0 - uTime * 0.55)) * 0.6 + n(vec2(vUv.x * 13.0, h * 9.0 - uTime * 1.1)) * 0.4;
        float alpha = smoothstep(0.0, 0.25, h) * pow(1.0 - h, 1.25) * smoothstep(0.22, 0.72, a) * vMix.y;
        if (alpha < 0.01) discard;
        vec3 col = mix(vec3(vMix.z), vec3(vMix.z * 0.8 + 0.12), h);
        gl_FragColor = vec4(col, alpha * 0.85);
      }`,
  });

/** The board's ground copied and lifted: black behind the great fire's line (`uFront`, world z) and where embers burnt (`uSpots`: x, z, r). */
const scorchMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uTime: { value: 0 }, uFront: { value: 1e9 }, uSpots: { value: Array.from({ length: 8 }, () => new THREE.Vector3(0, 0, 0)) } },
    vertexShader: /* glsl */ `
      varying vec3 vW;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float uTime; uniform float uFront; uniform vec3 uSpots[8]; varying vec3 vW;
      float hh(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(hh(i), hh(i + vec2(1, 0)), u.x), mix(hh(i + vec2(0, 1)), hh(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        float burnt = smoothstep(uFront - 0.4, uFront + 0.6, vW.z);
        for (int i = 0; i < 8; i++) { vec3 s = uSpots[i]; if (s.z > 0.0) burnt = max(burnt, smoothstep(s.z, s.z * 0.7, distance(vW.xz, s.xy))); }
        if (burnt < 0.02) discard;
        float g = n(vW.xz * 3.0);
        vec3 col = mix(vec3(0.09, 0.08, 0.075), vec3(0.16, 0.14, 0.12), g);
        // Embers still glowing in the black, more near the line.
        float glow = smoothstep(0.82, 0.95, n(vW.xz * 5.0 + vec2(uTime * 0.3, 0.0))) * (0.4 + 0.6 * smoothstep(uFront + 3.0, uFront, vW.z));
        col = mix(col, vec3(1.0, 0.42, 0.12), glow * 0.8);
        gl_FragColor = vec4(col, burnt * 0.92);
      }`,
  });

export class HornosLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(260);
  private dome: SkyDome | null = null;
  private ash: Precip | null = null;
  private terrain: TerrainBuild | null = null;
  private now = 0;
  private look = new THREE.Vector3();
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private lastState: BattleState | null = null;
  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private e3 = new THREE.Euler();
  // Shared dynamic pieces.
  private smoke: THREE.InstancedMesh | null = null;
  private smokeMix: THREE.InstancedBufferAttribute | null = null;
  private smokeMatU: THREE.ShaderMaterial | null = null;
  private smokeN = 0;
  private flames: THREE.InstancedMesh | null = null;
  private rings: THREE.InstancedMesh | null = null;
  private lean = new THREE.Vector2(0, -0.35);
  private leanTo = new THREE.Vector2(0, -0.35);
  // The kilns.
  private Kl: KilnLayout | null = null;
  private kilnAt: THREE.Vector3[] = [];
  private domes: THREE.InstancedMesh | null = null;
  private caps: THREE.InstancedMesh | null = null;
  private craters: THREE.InstancedMesh | null = null;
  private kilnSmoke: number[] = [];
  private kilnKey = '';
  private tuftKey = '';
  private stripKey = '';
  // The strip.
  private Pl: PineLayout | null = null;
  private treeAt: THREE.Vector3[] = [];
  private standing: THREE.InstancedMesh | null = null;
  private logs: THREE.InstancedMesh | null = null;
  private scorch: THREE.ShaderMaterial | null = null;
  // The counterfire.
  private Cl: CounterLayout | null = null;
  private tufts: THREE.InstancedMesh | null = null;
  private tuftOf: number[] = [];
  private tuftBase: THREE.Matrix4[] = [];
  private tuftCol: THREE.Color[] = [];
  private vertPatch: Int16Array | null = null;
  private patchSmoke: number[] = [];
  private breathLine: THREE.Mesh | null = null;
  private arrows: THREE.Mesh[] = [];
  private arrowMat: THREE.MeshBasicMaterial | null = null;
  private arrowTurn = 0;
  private breathMat: THREE.MeshBasicMaterial | null = null;
  // The pine.
  private pineAt: THREE.Vector3 | null = null;
  private coneShut: THREE.Mesh | null = null;
  private coneOpen: THREE.Group | null = null;
  private faceAt: THREE.Vector3 | null = null;
  private scorchAt: THREE.Vector3[] = [];
  private coneFlash = 0;
  private hour: Hour = 'day';

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `hornos-${place}`;
    const kit = new Kit();
    this.shared();
    if (place === 'kilnSlope' || place === 'kilnCine') this.buildKilns(kit, s, place === 'kilnCine');
    else if (place === 'oldPine' || place === 'pineFace') this.buildPine(kit, s, place === 'pineFace');
    else if (place === 'counterSlope') this.buildCounter(kit, s);
    else this.buildRock(kit);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.03, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'hornos-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    // Ash in the air, drifting.
    this.ash = new Precip({ count: ctx.low ? 160 : 420, box: [14, 8, 14], fall: 0.7, wind: [0.5, 0.2], size: [0.035, 0.035], color: '#d6cec6', opacity: 0.55 }, 53);
    this.group.add(this.ash.mesh);
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
  }

  /** A small piece built once from the kit's parts (its vertex colours), to be instanced. */
  private merged(fill: (k: Kit) => void): THREE.BufferGeometry {
    const k = new Kit();
    fill(k);
    const m = k.build(this.vc(), 'hornos-piece');
    return this.track((m as THREE.Mesh).geometry);
  }

  private vcMat: THREE.MeshStandardMaterial | null = null;
  private vc(): THREE.MeshStandardMaterial {
    if (!this.vcMat) this.vcMat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.02, flatShading: true }));
    return this.vcMat;
  }

  /** A kiln's mound (unit: a dome of radius 0.5): dark earth, turf sods laid on it, a vent of stones at the top. */
  private moundGeo(cap: boolean): THREE.BufferGeometry {
    return this.merged((k) => {
      const dome = new THREE.SphereGeometry(0.5, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const ico = new THREE.IcosahedronGeometry(0.5, 0);
      k.add(dome, cap ? '#5e4636' : '#36302c', place(0, 0, 0, 0, 1));
      const sods = cap ? ['#6e543e', '#52402e', '#7a5e44'] : ['#4e6030', '#42502e', '#4a4036', '#5a6a36'];
      for (let i = 0; i < 16; i++) {
        const a = i * 2.39;
        const e = 0.25 + ((i * 7) % 5) * 0.12;
        const r = Math.cos(e) * 0.5;
        k.add(ico, sods[i % sods.length] as string, place(Math.cos(a) * r, Math.sin(e) * 0.5, Math.sin(a) * r, a, new THREE.Vector3(0.2, 0.06, 0.16), 0, -e));
      }
      if (!cap) {
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          k.add(ico, ['#8a847c', '#77726b'][i % 2] as string, place(Math.cos(a) * 0.08, 0.5, Math.sin(a) * 0.08, a, new THREE.Vector3(0.06, 0.05, 0.05)));
        }
        k.add(dome, '#1c1816', place(0, 0.49, 0, 0, new THREE.Vector3(0.12, 0.04, 0.12)));
      }
      dome.dispose();
      ico.dispose();
    });
  }

  /** A burst kiln: a ring of thrown earth, charcoal black in the middle. */
  private craterGeo(): THREE.BufferGeometry {
    return this.merged((k) => {
      const torus = new THREE.TorusGeometry(0.6, 0.2, 6, 14).rotateX(Math.PI / 2);
      const ico = new THREE.IcosahedronGeometry(0.5, 0);
      const disc = new THREE.CylinderGeometry(0.5, 0.5, 0.04, 12);
      k.add(torus, '#4a3a2e', place(0, 0, 0, 0, 1));
      k.add(disc, '#1c1a19', place(0, 0.01, 0, 0, new THREE.Vector3(1.3, 1, 1.3)));
      for (let i = 0; i < 12; i++) {
        const a = i * 2.39;
        const d = 0.7 + (i % 4) * 0.18;
        k.add(ico, i % 3 === 0 ? '#1c1a19' : (EARTH[i % 3] as string), place(Math.cos(a) * d, 0.04, Math.sin(a) * d, a, new THREE.Vector3(0.18, 0.1, 0.14)));
      }
      torus.dispose();
      ico.dispose();
      disc.dispose();
    });
  }

  /** A tuft of dry grass: thin blades leaning out (unit, instanced over the patches). */
  private tuftGeo(): THREE.BufferGeometry {
    return this.merged((k) => {
      const blade = new THREE.ConeGeometry(0.045, 0.7, 3).translate(0, 0.35, 0);
      for (let i = 0; i < 9; i++) {
        const a = i * 2.39;
        const d = 0.06 + (i % 3) * 0.08;
        k.add(blade, ['#ffffff', '#e6e0d4', '#f4f0e8'][i % 3] as string, place(Math.cos(a) * d, 0, Math.sin(a) * d, a, new THREE.Vector3(1, 0.8 + (i % 4) * 0.15, 1), 0.38 * Math.cos(a), 0.38 * Math.sin(a)));
      }
      blade.dispose();
    });
  }

  /** The smoke, the flames and the rings every place may use. */
  private shared(): void {
    const sm = this.track(smokeMat());
    this.smokeMatU = sm;
    const sg = this.track(new THREE.CylinderGeometry(0.5, 0.5, 1, 10, 6, true).translate(0, 0.5, 0));
    const N = 28;
    const mix = new THREE.InstancedBufferAttribute(new Float32Array(N * 3), 3);
    sg.setAttribute('aMix', mix);
    const smoke = new THREE.InstancedMesh(sg, sm, N);
    smoke.count = 0;
    smoke.frustumCulled = false;
    smoke.renderOrder = 6;
    smoke.name = 'hornos-smoke';
    this.smoke = smoke;
    this.smokeMix = mix;
    this.group.add(smoke);
    // Flames: a tall cone, glowing, added over what is behind.
    const fg = this.track(new THREE.ConeGeometry(0.17, 0.9, 7, 2, false).translate(0, 0.45, 0));
    const fm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.92, depthWrite: false, side: THREE.DoubleSide, toneMapped: false }));
    const flames = new THREE.InstancedMesh(fg, fm, 280);
    flames.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(280 * 3), 3);
    flames.count = 0;
    flames.frustumCulled = false;
    flames.renderOrder = 7;
    flames.name = 'hornos-flames';
    this.flames = flames;
    this.group.add(flames);
    const rg = this.track(new THREE.RingGeometry(0.86, 1, 40).rotateX(-Math.PI / 2));
    const rm = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.9, depthWrite: false, side: THREE.DoubleSide }));
    const rings = new THREE.InstancedMesh(rg, rm, 12);
    rings.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(12 * 3), 3);
    rings.count = 0;
    rings.frustumCulled = false;
    rings.renderOrder = 5;
    rings.name = 'hornos-rings';
    this.rings = rings;
    this.group.add(rings);
  }

  /** A column of smoke at a point (its index for later): `k` its strength, `grey` its shade, `tall` its height. */
  private addSmoke(p: THREE.Vector3, k: number, grey: number, tall: number, width = 0.5): number {
    const sm = this.smoke as THREE.InstancedMesh;
    const i = this.smokeN++;
    if (i >= sm.instanceMatrix.count) return -1;
    this.m4.compose(p, this.q4.identity(), this.s3.set(width, tall, width));
    sm.setMatrixAt(i, this.m4);
    this.setSmoke(i, k, grey);
    sm.count = this.smokeN;
    sm.instanceMatrix.needsUpdate = true;
    return i;
  }

  private setSmoke(i: number, k: number, grey: number): void {
    const mix = this.smokeMix;
    if (!mix || i < 0) return;
    // (Written only when it changes: the columns are set every tick and almost never move.)
    if (mix.getY(i) === k && mix.getZ(i) === grey) return;
    mix.setXYZ(i, (i * 0.37) % 1, k, grey);
    mix.needsUpdate = true;
  }

  /** Tall dry grass: a few blades round a point (merged). */
  private grass(kit: Kit, p: THREE.Vector3, n: number, seed: number, color = GRASS, tall = 1): void {
    const cone = new THREE.ConeGeometry(0.07, 0.75, 3);
    for (let i = 0; i < n; i++) {
      const a = seed * 2.3 + i * 2.39;
      const d = 0.12 + ((i * 37 + seed * 11) % 10) * 0.06;
      kit.add(cone, color[(i + seed) % color.length] as string, place(p.x + Math.cos(a) * d, p.y + 0.3 * tall, p.z + Math.sin(a) * d, a, new THREE.Vector3(1, (0.75 + (i % 3) * 0.28) * tall, 1), 0.32 * Math.cos(a), 0.32 * Math.sin(a)));
    }
    cone.dispose();
  }

  /** A pine of the slope: a straight trunk, its crown in tiers. */
  private pine(kit: Kit, p: THREE.Vector3, k: number, tall = 1): void {
    const cyl = new THREE.CylinderGeometry(0.09, 0.15, 1, 6);
    const cone = new THREE.ConeGeometry(0.7, 1, 7);
    const h = (2.6 + k * 1.4) * tall;
    kit.add(cyl, BARK[Math.floor(k * 7) % 3] as string, place(p.x, p.y + h * 0.4, p.z, k * 5, new THREE.Vector3(1, h * 0.8, 1)));
    for (let i = 0; i < 4; i++) {
      const y = p.y + h * (0.42 + i * 0.16);
      const w = (1.25 - i * 0.24) * tall;
      kit.add(cone, PINE[(i + Math.floor(k * 5)) % 3] as string, place(p.x, y, p.z, k * 3 + i, new THREE.Vector3(w, h * 0.3, w)));
    }
    cyl.dispose();
    cone.dispose();
  }

  /** Olmo's face carved in a stone or in bark, looking south: the oval, the eyes, the mouth; the words under it. */
  private face(kit: Kit, p: THREE.Vector3, size: number, stone: boolean): void {
    const sph = new THREE.SphereGeometry(0.42, 12, 8);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const base = stone ? '#a8a29a' : '#8e7a66';
    const cut = stone ? '#2e2a26' : '#2a1a12';
    kit.add(sph, base, place(p.x, p.y, p.z, 0, new THREE.Vector3(1.4 * size, 1.9 * size, 0.28 * size)));
    // Brow and eyes: a ridge, two deep slots; the nose; the mouth a groove; the words under it.
    kit.add(box, stone ? '#b8b2aa' : '#9c8872', place(p.x, p.y + 0.3 * size, p.z + 0.1 * size, 0, new THREE.Vector3(0.5 * size, 0.06 * size, 0.08 * size)));
    for (const k of [-1, 1]) kit.add(sph, cut, place(p.x + k * 0.18 * size, p.y + 0.16 * size, p.z + 0.1 * size, 0, new THREE.Vector3(0.22 * size, 0.09 * size, 0.08 * size)));
    kit.add(box, cut, place(p.x, p.y - 0.28 * size, p.z + 0.11 * size, 0, new THREE.Vector3(0.3 * size, 0.04 * size, 0.05 * size)));
    kit.add(box, stone ? '#958f87' : '#7a6652', place(p.x, p.y - 0.02 * size, p.z + 0.12 * size, 0, new THREE.Vector3(0.07 * size, 0.24 * size, 0.07 * size)));
    for (let i = 0; i < 2; i++) kit.add(box, cut, place(p.x, p.y - (0.6 + i * 0.12) * size, p.z + 0.06 * size, 0, new THREE.Vector3((0.5 - i * 0.14) * size, 0.03 * size, 0.04 * size)));
    sph.dispose();
    box.dispose();
  }

  /** Los Hornos down in the valley: houses of stone with dark roofs, a chimney each, lit windows at dusk. */
  private village(kit: Kit, cx: number, cz: number, y: number, lit: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const roof = new THREE.ConeGeometry(0.75, 0.6, 4);
    for (let i = 0; i < 22; i++) {
      const a = i * 2.39;
      const d = 1.2 + (i % 7) * 0.75;
      const x = cx + Math.cos(a) * d * 1.6;
      const z = cz + Math.sin(a) * d * 0.7 + (i % 3) * 0.4;
      const w = 0.8 + (i % 3) * 0.2;
      kit.add(box, STONE[i % 3] as string, place(x, y + 0.35, z, a * 0.3, new THREE.Vector3(w, 0.7, w * 0.8)));
      kit.add(roof, ['#3a3432', '#4a3a34', '#2e2a2a'][i % 3] as string, place(x, y + 0.98, z, a * 0.3 + Math.PI / 4, new THREE.Vector3(w * 1.05, 1, w * 0.85)));
      kit.add(box, '#4a403a', place(x + w * 0.25, y + 1.15, z, 0, new THREE.Vector3(0.12, 0.4, 0.12)));
      if (lit && i % 2 === 0) this.light(new THREE.Vector3(x, y + 0.45, z + w * 0.42), '#ffb060', 0.7, 0.9, 1);
    }
    box.dispose();
    roof.dispose();
  }

  /**
   * The old pine: a tall straight trunk, dark and furrowed at its foot and orange higher up (an old Scots pine), its
   * roots gripping the rock, a few dead stubs; its crown high and flat in irregular tiers of dark needles; the great
   * cone hanging from the lowest bough; Olmo's second face carved in the bark at the height of a man.
   */
  private oldPine(kit: Kit, p: THREE.Vector3, open: boolean, faceGlow: boolean): void {
    const cyl = new THREE.CylinderGeometry(0.5, 0.62, 1, 9);
    const cone = new THREE.ConeGeometry(1, 1, 8);
    const ico = new THREE.IcosahedronGeometry(0.5, 1);
    const ico0 = new THREE.IcosahedronGeometry(0.5, 0);
    // The trunk: three lengths, narrowing and warming upward, a little lean.
    kit.add(cyl, '#4e3426', place(p.x, p.y + 1.1, p.z, 0.2, new THREE.Vector3(1.15, 2.2, 1.1)));
    kit.add(cyl, '#7a4a2e', place(p.x + 0.05, p.y + 3.3, p.z, 0.5, new THREE.Vector3(0.85, 2.4, 0.82)));
    kit.add(cyl, '#a06038', place(p.x + 0.14, p.y + 5.4, p.z - 0.05, 0.9, new THREE.Vector3(0.6, 2.0, 0.58), 0, -0.04));
    // Its furrows: dark strakes down the foot.
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2;
      kit.add(cyl, '#2e2018', place(p.x + Math.cos(a) * 0.54, p.y + 1.1, p.z + Math.sin(a) * 0.52, a, new THREE.Vector3(0.08, 2.0, 0.08)));
    }
    // Roots over the rock.
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.3;
      kit.add(cyl, BARK[i % 3] as string, place(p.x + Math.cos(a) * 0.85, p.y + 0.12, p.z + Math.sin(a) * 0.8, a, new THREE.Vector3(0.34, 1.2, 0.34), Math.PI / 2 - 0.3, 0));
    }
    // Dead stubs low on the trunk.
    for (let i = 0; i < 3; i++) {
      const a = i * 2.2 + 0.7;
      kit.add(cyl, '#5a4434', place(p.x + Math.cos(a) * 0.45, p.y + 2.4 + i * 0.7, p.z + Math.sin(a) * 0.45, a, new THREE.Vector3(0.1, 0.8, 0.1), 0, Math.PI / 2 - 0.3));
    }
    // Boughs out from the top, and the crown in flat tiers of needles (dark, a lighter top).
    const tiers: [number, number, number, number, number][] = [
      [1.4, 5.0, 0.3, 2.0, 0.42],
      [-1.3, 5.4, -0.1, 1.9, 0.4],
      [0.5, 5.9, 0.5, 2.1, 0.42],
      [-0.6, 6.4, -0.4, 1.8, 0.4],
      [0.9, 6.9, -0.2, 1.6, 0.38],
      [-0.5, 7.3, 0.3, 1.5, 0.36],
      [0.2, 7.8, 0, 1.1, 0.34],
    ];
    tiers.forEach(([x, y, z, w, h], i) => {
      const a = Math.atan2(z, x);
      kit.add(cyl, '#7a4a2e', place(p.x + x * 0.5, p.y + y - 0.25, p.z + z * 0.5, -a, new THREE.Vector3(0.14, Math.hypot(x, z) + 0.3, 0.14), 0, Math.PI / 2 - 0.35));
      kit.add(ico, ['#243a2a', '#2c4430', '#20342a'][i % 3] as string, place(p.x + x, p.y + y, p.z + z * 0.6, i * 0.9, new THREE.Vector3(w, h, w * 0.6)));
      kit.add(ico, ['#34503a', '#3c5a3e', '#2e4a34'][i % 3] as string, place(p.x + x + 0.1, p.y + y + h * 0.35, p.z + z * 0.6 - 0.05, i * 0.9 + 0.4, new THREE.Vector3(w * 0.72, h * 0.6, w * 0.45)));
      // Tufts of needles hanging off its rim.
      for (let k = 0; k < 4; k++) {
        const b = k * 1.6 + i;
        kit.add(cone, '#2a4230', place(p.x + x + Math.cos(b) * w * 0.42, p.y + y - h * 0.3, p.z + z * 0.6 + Math.sin(b) * w * 0.25, b, new THREE.Vector3(0.22, 0.32, 0.22), Math.PI, 0));
      }
    });
    // Its rock: pale stone round its foot, needles and cones on it.
    for (let i = 0; i < 11; i++) {
      const a = (i / 11) * Math.PI * 2;
      kit.add(ico0, STONE[i % 3] as string, place(p.x + Math.cos(a) * 1.9, p.y + 0.15, p.z + Math.sin(a) * 1.6, a, new THREE.Vector3(0.95, 0.5, 0.75)));
    }
    for (let i = 0; i < 5; i++) kit.add(ico0, '#6a4a30', place(p.x + Math.cos(i * 1.9) * 1.3, p.y + 0.08, p.z + Math.sin(i * 1.9) * 1.1 + 0.3, i, new THREE.Vector3(0.14, 0.2, 0.14)));
    // The face, carved in the bark on its south side: a smoothed oval a shade paler than the bark, cut deep.
    const f = new THREE.Vector3(p.x, p.y + 1.55, p.z + 0.6);
    this.face(kit, f, 0.62, false);
    this.faceAt = f;
    if (faceGlow) this.light(f.clone().setZ(f.z + 0.2), '#fff4e4', 1.4, 0.45);
    // The great cone, hanging under the lowest tier: shut (dark scales), or open with its winged seed.
    const cm = this.track(std('#6a4a30', { roughness: 0.85, flatShading: true }));
    const shut = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.42, 10, 8)), cm);
    shut.scale.set(0.8, 1.3, 0.8);
    shut.position.set(p.x + 1.2, p.y + 4.4, p.z + 0.7);
    shut.name = 'hornos-cone';
    this.group.add(shut);
    this.coneShut = shut;
    const og = new THREE.Group();
    og.position.copy(shut.position);
    const scale = this.track(new THREE.ConeGeometry(0.16, 0.42, 4));
    for (let i = 0; i < 14; i++) {
      const sc = new THREE.Mesh(scale, cm);
      const a = i * 2.39;
      const y = -0.4 + (i / 14) * 0.8;
      sc.position.set(Math.cos(a) * 0.25, y, Math.sin(a) * 0.25);
      sc.rotation.set(Math.sin(a) * 1.1, 0, -Math.cos(a) * 1.1);
      og.add(sc);
    }
    const seed = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.09, 8, 6)), this.track(std('#3a2a1c', { roughness: 0.5, emissive: '#5a3a1a', emissiveIntensity: 0.5 })));
    seed.position.set(0, -0.6, 0.15);
    const wing = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.34, 0.12)), this.track(std('#c8b490', { roughness: 0.7, side: THREE.DoubleSide, emissive: '#3a2a10', emissiveIntensity: 0.4 })));
    wing.position.set(0.15, -0.55, 0.15);
    wing.rotation.z = 0.4;
    og.add(seed, wing);
    og.visible = false;
    og.name = 'hornos-cone-open';
    this.group.add(og);
    this.coneOpen = og;
    if (open) {
      shut.visible = false;
      og.visible = true;
    }
    this.pineAt = p.clone();
    this.scorchAt = [0, 1, 2].map((i) => new THREE.Vector3(p.x + (i - 1) * 0.4, p.y + 0.9 + i * 0.8, p.z + 0.62));
    cyl.dispose();
    cone.dispose();
    ico.dispose();
    ico0.dispose();
  }

  /** Pines thick past the board's rim (outside `R`), every so often; outcrops among them. */
  private rim(kit: Kit, R: number, every: number, skip?: (x: number, z: number) => boolean): void {
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (let r = -R - 4; r <= R + 4; r++)
      for (let q = -R - 4; q <= R + 4; q++) {
        const d = Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
        if (d <= R || d > R + 3) continue;
        const k = Math.sin(q * 12.9 + r * 78.2) * 0.5 + 0.5;
        const p = this.hex(q, r);
        if (skip?.(p.x, p.z)) continue;
        if ((q * 7 + r * 13 + 99) % every === 0) this.pine(kit, p, k, d === R + 1 ? 0.8 : 1.05);
        else if ((q * 5 + r * 3 + 40) % 5 === 0) kit.add(ico, STONE[(q + r + 30) % 3] as string, place(p.x, p.y + 0.2, p.z, k * 6, new THREE.Vector3(1.0, 0.6, 0.8)));
        else this.grass(kit, p, 3, q * 3 + r, GRASS, 1.2);
      }
    ico.dispose();
  }

  /* ---------- the kilns ---------- */

  private buildKilns(kit: Kit, s: BattleState, cine: boolean): void {
    const L = kilnLayout(s.seed, DRUID_PATH.kilns.n);
    this.Kl = L;
    const R = L.radius;
    for (const h of L.grass) this.grass(kit, this.hex(h.q, h.r), (h.q * 5 + h.r * 11 + 77) % 3 === 0 ? 7 : 4, h.q * 3 + h.r * 7);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (const h of L.outcrops) {
      const p = this.hex(h.q, h.r);
      kit.add(ico, STONE[(h.q + h.r + 30) % 3] as string, place(p.x, p.y + 0.3, p.z, h.q, new THREE.Vector3(1.4, 0.8, 1.2)));
      kit.add(ico, STONE[(h.q + 31) % 3] as string, place(p.x + 0.4, p.y + 0.2, p.z + 0.3, h.r, new THREE.Vector3(0.7, 0.5, 0.6)));
    }
    // The kilns: each a dome of dark earth (instanced, its state drawn each tick), a ring of turf round its foot, a
    // stone mouth on its south side with Olmo's face carved on it (the family's mark).
    for (const h of L.kilns) {
      const p = this.hex(h.q, h.r);
      this.kilnAt.push(p);
      // Sods of turf round its foot, the poles of its stack showing, its mouth of stone at the south.
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        kit.add(ico, (i % 2 ? GREEN_GRASS : EARTH)[i % 3] as string, place(p.x + Math.cos(a) * 1.18, p.y + 0.08, p.z + Math.sin(a) * 1.08, a, new THREE.Vector3(0.55, 0.26, 0.45)));
      }
      kit.add(ico, STONE[1] as string, place(p.x, p.y + 0.32, p.z + 1.12, 0, new THREE.Vector3(0.78, 0.7, 0.32)));
      kit.add(ico, STONE[0] as string, place(p.x - 0.38, p.y + 0.2, p.z + 1.05, 0.4, new THREE.Vector3(0.4, 0.4, 0.3)));
      kit.add(ico, STONE[2] as string, place(p.x + 0.4, p.y + 0.2, p.z + 1.05, 0.9, new THREE.Vector3(0.4, 0.42, 0.3)));
      this.face(kit, new THREE.Vector3(p.x, p.y + 0.38, p.z + 1.27), 0.3, true);
      this.kilnSmoke.push(this.addSmoke(new THREE.Vector3(p.x, p.y + 1.15, p.z), 0.6, 0.55, 6, 0.5));
    }
    ico.dispose();
    const n = L.kilns.length;
    const mk = (geo: THREE.BufferGeometry, _color: string, name: string): THREE.InstancedMesh => {
      const m = new THREE.InstancedMesh(geo, this.vc(), n);
      m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
      m.frustumCulled = false;
      m.castShadow = true;
      m.receiveShadow = true;
      m.name = name;
      this.group.add(m);
      return m;
    };
    this.domes = mk(this.moundGeo(false), '#ffffff', 'hornos-domes');
    this.caps = mk(this.moundGeo(true), '#ffffff', 'hornos-caps');
    this.craters = mk(this.craterGeo(), '#ffffff', 'hornos-craters');
    // Up the slope, the old pine with its cone; down in the valley, Los Hornos and the fire coming up.
    this.rim(kit, R, 2, (x, z) => z < -(R - 1) * S && Math.abs(x) < 5);
    this.oldPine(kit, this.ctx.at(1200, -(R + 4) * 866, 0), false, false);
    this.village(kit, 0, (R + 9) * S, this.ctx.ground(0, 0) - 1.2, cine);
    for (let i = 0; i < 6; i++) this.addSmoke(this.ctx.at(-9000 + i * 3600, (R + 7 + (i % 2) * 2) * 866, 0), 0.8, 0.45, 9, 1.2);
    if (cine) for (let i = 0; i < 5; i++) this.light(this.ctx.at(-8000 + i * 4000, (R + 12) * 866, 0.4), '#ff6a2a', 5, 0.8, 1);
    this.drawKilns(null);
  }

  private drawKilns(s: BattleState | null): void {
    const L = this.Kl;
    if (!L || !this.domes || !this.caps || !this.craters) return;
    const w = s?.senda?.summer;
    // (Written only when a kiln changes, or a cracking one pulses: docs/RENDIMIENTO.md, «Un buffer instanciado…».)
    const key = `${w ? w.kiln.join('') : ''}|${w && w.kiln.includes(2) ? Math.floor(this.now * 4) : 0}`;
    if (key === this.kilnKey) return;
    this.kilnKey = key;
    for (let k = 0; k < this.kilnAt.length; k++) {
      const p = this.kilnAt[k] as THREE.Vector3;
      const v = w ? (w.kiln[k] as number) : 0;
      const hide = this.s3.set(0.001, 0.001, 0.001);
      // The dome: dark earth; alight its top reddens; cracking it glows through.
      const pulse = 0.5 + 0.5 * Math.sin(this.now * 9);
      this.m4.compose(this.v3.set(p.x, p.y - 0.02, p.z), this.q4.identity(), v === 3 || v === 4 ? hide : this.s3.set(2.3, 2.2, 2.1));
      this.domes.setMatrixAt(k, this.m4);
      this.domes.setColorAt(k, this.c3.set(v === 2 ? (pulse > 0.5 ? '#ff8a70' : '#d86a50') : v === 1 ? '#ffc8a8' : '#ffffff'));
      this.m4.compose(this.v3.set(p.x, p.y - 0.02, p.z), this.q4.identity(), v === 4 ? this.s3.set(2.45, 2.3, 2.25) : hide);
      this.caps.setMatrixAt(k, this.m4);
      this.caps.setColorAt(k, this.c3.set('#ffffff'));
      this.m4.compose(this.v3.set(p.x, p.y + 0.05, p.z), this.q4.identity(), v === 3 ? this.s3.set(1.4, 1, 1.3) : hide);
      this.craters.setMatrixAt(k, this.m4);
      this.craters.setColorAt(k, this.c3.set('#ffffff'));
      void pulse;
      // Its smoke: thin grey smoking, black alight, white cracking, none burst, thin over fresh earth.
      const si = this.kilnSmoke[k] ?? -1;
      if (v === 0) this.setSmoke(si, 0.6, 0.6);
      else if (v === 1) this.setSmoke(si, 0.95, 0.22);
      else if (v === 2) this.setSmoke(si, 1.0, 0.95);
      else if (v === 3) this.setSmoke(si, 0.2, 0.15);
      else this.setSmoke(si, 0.3, 0.7);
    }
    for (const m of [this.domes, this.caps, this.craters]) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
  }

  /* ---------- Olmo's pine and the strip ---------- */

  private buildPine(kit: Kit, s: BattleState, close: boolean): void {
    const L = pineLayout(s.seed, DRUID_PATH.strip.trees);
    this.Pl = L;
    const R = L.radius;
    const pine = new Set(L.pine.map((h) => `${h.q},${h.r}`));
    for (const h of L.grass) {
      if (pine.has(`${h.q},${h.r}`)) continue;
      // (The close: below the strip the fire went through; black stubble only.)
      if (close && h.r > L.strip) this.grass(kit, this.hex(h.q, h.r), 3, h.q * 3 + h.r * 7, ['#1c1a19', '#2a2420'], 0.25);
      else this.grass(kit, this.hex(h.q, h.r), (h.q * 5 + h.r * 11 + 77) % 3 === 0 ? 7 : 4, h.q * 3 + h.r * 7, h.r < L.strip ? GREEN_GRASS : GRASS);
    }
    const t = this.hex(L.trunk.q, L.trunk.r);
    this.oldPine(kit, t, close, close);
    // The strip's trees: young pines standing in a row (instanced: standing, or a log and its stump once felled).
    this.treeAt = L.trees.map((h) => this.hex(h.q, h.r));
    const n = L.trees.length;
    const tg = this.track(new THREE.ConeGeometry(0.62, 1, 7).translate(0, 0.5, 0));
    const st = new THREE.InstancedMesh(tg, this.track(std('#ffffff', { roughness: 0.9, flatShading: true })), n * 4);
    st.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 4 * 3), 3);
    st.frustumCulled = false;
    st.castShadow = true;
    st.name = 'hornos-trees';
    this.standing = st;
    this.group.add(st);
    const lg = this.track(new THREE.CylinderGeometry(0.12, 0.16, 1, 7));
    const logs = new THREE.InstancedMesh(lg, this.track(std('#7a5238', { roughness: 0.9, flatShading: true })), n * 2);
    logs.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 2 * 3), 3);
    logs.frustumCulled = false;
    logs.castShadow = true;
    logs.name = 'hornos-logs';
    this.logs = logs;
    this.group.add(logs);
    this.rim(kit, R, 2);
    // Down in the valley: Los Hornos, and the smoke of the great fire coming up.
    this.village(kit, 0, (R + 9) * S, this.ctx.ground(0, 0) - 1.4, close);
    for (let i = 0; i < 6; i++) this.addSmoke(this.ctx.at(-9000 + i * 3600, (R + 4 + (i % 2) * 2) * 866, 0), 0.9, 0.35, 11, 1.4);
    // The close: the slope burnt below the strip, the kilns smoking safe in a row far down.
    if (close) {
      for (let i = 0; i < 6; i++) this.addSmoke(this.ctx.at(-6000 + i * 2400, (R + 5) * 866, 0), 0.55, 0.65, 6, 0.4);
    }
    this.drawStrip(null);
  }

  private drawStrip(s: BattleState | null): void {
    const L = this.Pl;
    const st = this.standing;
    const logs = this.logs;
    if (!L || !st || !logs) return;
    const hob = s?.senda?.npcs.find((n) => n.id === 'hob');
    const felled = this.place === 'pineFace' ? L.trees.length : Math.min(L.trees.length, hob?.spot ?? 0);
    if (String(felled) === this.stripKey) return;
    this.stripKey = String(felled);
    let a = 0;
    let b = 0;
    this.treeAt.forEach((p, i) => {
      if (i < felled) {
        // A log lying downhill and its stump.
        this.q4.setFromEuler(this.e3.set(Math.PI / 2, 0.3 + i * 0.7, 0));
        this.m4.compose(this.v3.set(p.x + 0.3, p.y + 0.16, p.z + 0.7), this.q4, this.s3.set(1, 2.4, 1));
        logs.setMatrixAt(b, this.m4);
        logs.setColorAt(b++, this.c3.set('#7a5238'));
        this.m4.compose(this.v3.set(p.x, p.y + 0.18, p.z), this.q4.identity(), this.s3.set(1.4, 0.35, 1.4));
        logs.setMatrixAt(b, this.m4);
        logs.setColorAt(b++, this.c3.set('#b08a60'));
        return;
      }
      // Standing: a trunk and three tiers.
      this.m4.compose(this.v3.set(p.x, p.y, p.z), this.q4.identity(), this.s3.set(0.22, 1.4, 0.22));
      st.setMatrixAt(a, this.m4);
      st.setColorAt(a++, this.c3.set(BARK[0] as string));
      for (let k = 0; k < 3; k++) {
        this.m4.compose(this.v3.set(p.x, p.y + 0.9 + k * 0.62, p.z), this.q4.identity(), this.s3.set(1.5 - k * 0.38, 1.1, 1.5 - k * 0.38));
        st.setMatrixAt(a, this.m4);
        st.setColorAt(a++, this.c3.set(PINE[(i + k) % 3] as string));
      }
    });
    st.count = a;
    logs.count = b;
    for (const m of [st, logs]) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
  }

  /* ---------- the counterfire ---------- */

  private buildCounter(kit: Kit, s: BattleState): void {
    const C = DRUID_PATH.counter;
    const L = counterLayout(s.seed, { burn: C.burn, catch: C.catch, climb: C.climb, flipMin: C.flipMin, flipMax: C.flipMax, room: C.room });
    this.Cl = L;
    const R = L.radius;
    // The pine's band: bare pale stone, the pine on it.
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    for (const h of L.stone) {
      const p = this.hex(h.q, h.r);
      if ((h.q * 3 + h.r * 7 + 40) % 3 === 0) kit.add(ico, STONE[(h.q + h.r + 30) % 3] as string, place(p.x + 0.2, p.y + 0.08, p.z, h.q, new THREE.Vector3(0.7, 0.25, 0.55)));
    }
    ico.dispose();
    this.oldPine(kit, this.hex(L.pine.q, L.pine.r), false, false);
    // The grass of the patches (instanced: its tuft each cell, two per cell), tinted by the patch.
    const tones = ['#bcac58', '#9c9a4c', '#b49650'];
    const n = L.net.cells.reduce((a, c) => a + c.length * 2, 0);
    const tufts = new THREE.InstancedMesh(this.tuftGeo(), this.vc(), n);
    tufts.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
    tufts.frustumCulled = false;
    tufts.castShadow = true;
    tufts.name = 'hornos-tufts';
    let i = 0;
    L.net.cells.forEach((cells, pi) => {
      const tone = tones[((L.net.band[pi] as number) + pi) % 3] as string;
      for (const h of cells)
        for (let k = 0; k < 2; k++) {
          const p = this.hex(h.q, h.r);
          const a = h.q * 1.7 + h.r * 2.9 + k * 3.1;
          const m = new THREE.Matrix4().compose(new THREE.Vector3(p.x + Math.cos(a) * 0.3, p.y, p.z + Math.sin(a) * 0.25), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, a, 0)), new THREE.Vector3(1 + (k * 0.2), 1 + ((h.q + h.r + k) % 3) * 0.15, 1));
          tufts.setMatrixAt(i, m);
          const c = new THREE.Color(tone).offsetHSL(0, 0, ((h.q * 7 + h.r * 3 + k) % 5) * 0.012);
          tufts.setColorAt(i, c);
          this.tuftOf.push(pi);
          this.tuftBase.push(m);
          this.tuftCol.push(c);
          i++;
        }
      // Each patch's smoke, shown while it burns.
      const mid = L.mid[pi] as { x: number; y: number };
      this.patchSmoke.push(this.addSmoke(this.ctx.at(mid.x, mid.y, 0.4), 0, 0.3, 6, 0.6));
    });
    this.tufts = tufts;
    this.group.add(tufts);
    this.rim(kit, R, 2);
    // Down in the valley, the great fire's glow and smoke.
    for (let k = 0; k < 5; k++) {
      this.addSmoke(this.ctx.at(-8000 + k * 4000, (R + 3) * 866, 0), 0.9, 0.3, 12, 1.6);
      this.light(this.ctx.at(-8000 + k * 4000, (R + 2) * 866, 0.6), '#ff6a2a', 4.5, 0.6, 1);
    }
    // The wind's arrows on the stone band, either side of the pine: up the slope or down it (they flip with the wind).
    const shape = new THREE.Shape();
    shape.moveTo(-0.22, -0.7);
    shape.lineTo(0.22, -0.7);
    shape.lineTo(0.22, 0.1);
    shape.lineTo(0.5, 0.1);
    shape.lineTo(0, 0.75);
    shape.lineTo(-0.5, 0.1);
    shape.lineTo(-0.22, 0.1);
    shape.closePath();
    const ag = this.track(new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2));
    const am = this.track(new THREE.MeshBasicMaterial({ color: '#f2f4f6', transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide }));
    this.arrowMat = am;
    for (const x of [-4300, 4300]) {
      const a = new THREE.Mesh(ag, am);
      const c = this.ctx.at(x, L.pine.r * 866, 0.06);
      a.position.copy(c);
      a.scale.setScalar(1.5);
      a.renderOrder = 4;
      a.name = 'hornos-wind';
      this.arrows.push(a);
      this.group.add(a);
    }
    // The breath's line, warned on the ground.
    const bm = this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.4, depthWrite: false, side: THREE.DoubleSide }));
    const line = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2)), bm);
    line.visible = false;
    line.renderOrder = 4;
    line.name = 'hornos-breath';
    this.breathLine = line;
    this.breathMat = bm;
    this.group.add(line);
  }

  private drawCounter(s: BattleState): void {
    const L = this.Cl;
    const t = this.tufts;
    const w = s.senda?.summer;
    const r = s.rules?.senda?.summer?.counter;
    const st = w?.fire;
    if (!L || !t || !w || !st || !r) return;
    // The grass: dry and tall, alight (darker, a little shrunk), black (low stubble); written when a patch changes.
    const key = st.st.join('');
    const fresh = key !== this.tuftKey;
    this.tuftKey = key;
    for (let i = 0; fresh && i < this.tuftOf.length; i++) {
      const p = this.tuftOf[i] as number;
      const v = st.st[p] as number;
      const base = this.tuftBase[i] as THREE.Matrix4;
      if (v === 0) {
        t.setMatrixAt(i, base);
        t.setColorAt(i, this.tuftCol[i] as THREE.Color);
      } else {
        this.m4.copy(base);
        this.s3.set(1, v === 1 ? 0.7 : 0.18, 1);
        this.m4.scale(this.s3);
        t.setMatrixAt(i, this.m4);
        t.setColorAt(i, this.c3.set(v === 1 ? '#5a3220' : '#201c1a'));
      }
    }
    if (fresh) {
      t.instanceMatrix.needsUpdate = true;
      if (t.instanceColor) t.instanceColor.needsUpdate = true;
    }
    // Each patch's smoke while it burns (the great fire's thicker).
    st.st.forEach((v, p) => this.setSmoke(this.patchSmoke[p] ?? -1, v === 1 ? (st.grand[p] ? 1 : 0.8) : v === 2 ? 0.12 : 0, v === 1 ? 0.32 : 0.4));
    // The wind: the columns lean up the slope (−z) or down it, bending before it turns.
    const tt = w.t;
    const down = tt >= r.flip;
    const bend = !down && tt >= r.flip - r.warn;
    this.leanTo.set(bend ? 0.25 * Math.sin(this.now * 3) : 0, down ? 0.45 : bend ? 0.05 : -0.45);
    // The arrows: up the slope (−z) white; bending, red and swinging; down, white the other way.
    this.arrowTurn = down ? Math.PI : 0;
    if (this.arrowMat) this.arrowMat.color.set(bend ? '#ff4f3a' : '#f2f4f6');
    // The breath's line.
    if (this.breathLine && this.breathMat) {
      const on = w.bt >= 0;
      this.breathLine.visible = on;
      if (on) {
        const a = this.ctx.at(w.bx, w.by, 0.07);
        const b = this.ctx.at(w.bx2, w.by2, 0.07);
        const len = a.distanceTo(b);
        this.breathLine.position.set((a.x + b.x) / 2, Math.max(a.y, b.y), (a.z + b.z) / 2);
        this.breathLine.rotation.set(0, -Math.atan2(b.z - a.z, b.x - a.x), 0);
        this.breathLine.scale.set(len, 1, r.breath.w * K);
        const k = Math.max(0, Math.min(1, 1 - (w.bt - s.tick) / Math.max(1, r.breath.warn)));
        this.breathMat.opacity = 0.25 + 0.5 * k;
      }
    }
  }

  /** Paints a patch's ground black (once, when it burns out). */
  private blacken(p: number): void {
    const col = this.terrain?.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const vp = this.vertPatch;
    if (!col || !vp) return;
    const c = this.c3.set(CHAR);
    for (let i = 0; i < vp.length; i++) if (vp[i] === p) col.setXYZ(i, c.r * (0.9 + (i % 5) * 0.04), c.g, c.b);
    col.needsUpdate = true;
  }

  /* ---------- the campfire's rock ---------- */

  private buildRock(kit: Kit): void {
    const cyl = new THREE.CylinderGeometry(0.5, 0.6, 1, 10);
    const ico = new THREE.IcosahedronGeometry(0.5, 0);
    // The flat stone, its edges broken; pines behind; the valley burning below to the south.
    kit.add(cyl, STONE[0] as string, place(0, this.ctx.ground(0, 0) - 0.2, 0, 0.3, new THREE.Vector3(7.5, 0.5, 6.2)));
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2;
      kit.add(ico, STONE[i % 3] as string, place(Math.cos(a) * 3.6, 0.1, Math.sin(a) * 3.0, a, new THREE.Vector3(1.2, 0.6, 0.9)));
    }
    for (const [x, z, k] of [
      [-4.5, -3.5, 0.2],
      [4.2, -3.0, 0.7],
      [-5.2, 1.5, 0.5],
      [5.0, 2.0, 0.9],
      [-2.0, -5.0, 0.4],
      [2.5, -5.2, 0.6],
    ] as const)
      this.pine(kit, new THREE.Vector3(x, this.ctx.ground(x, z), z), k, 1.1);
    for (let i = 0; i < 8; i++) {
      const x = -12 + i * 3.4;
      this.addSmoke(new THREE.Vector3(x, -1.5, 9 + (i % 2) * 2), 0.9, 0.22, 10, 1.6);
      this.light(new THREE.Vector3(x, -0.8, 9 + (i % 2) * 2), '#ff5a24', 4, 0.75, 1);
    }
    cyl.dispose();
    ico.dispose();
  }

  /* ---------- painting ---------- */

  apply(terrain: TerrainBuild, hour: Hour = 'day'): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    // The counterfire's patches by cell, each its tone; the stone band pale.
    const patchOf = new Map<string, number>();
    const stone = new Set<string>();
    if (this.Cl) {
      this.Cl.net.cells.forEach((cs, p) => cs.forEach((h) => patchOf.set(`${h.q},${h.r}`, p)));
      for (const h of this.Cl.stone) stone.add(`${h.q},${h.r}`);
    }
    const kilnRock = new Set<string>();
    if (this.Kl) for (const h of this.Kl.outcrops) kilnRock.add(`${h.q},${h.r}`);
    const pineRock = new Set<string>();
    if (this.Pl) for (const h of this.Pl.pine) pineRock.add(`${h.q},${h.r}`);
    const tones = [
      ['#8c803e', '#968846', '#84783a'],
      ['#6c6834', '#76703a', '#646030'],
      ['#7e6a36', '#88743c', '#766232'],
    ];
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      const vp = new Int16Array(col.count).fill(-1);
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.3 + v.z * 0.7) * Math.cos(v.z * 1.1 - v.x * 0.4);
        const hq = Math.round(v.x / S - v.z / (S * 0.866) / 2);
        const hr = Math.round(v.z / (S * 0.866));
        const key = `${hq},${hr}`;
        const p = patchOf.get(key);
        if (this.place === 'nightRock') c.set('#3a3430').multiplyScalar(0.8 + lum * 0.3);
        else if (p !== undefined) {
          vp[i] = p;
          const band = this.Cl?.net.band[p] ?? 0;
          c.set((tones[(band + p) % 3] as string[])[Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        } else if (stone.has(key) || kilnRock.has(key) || pineRock.has(key)) c.set(STONE[Math.floor(k * 2.99)] as string).multiplyScalar(0.8 + lum * 0.3);
        else if (this.Pl && hr > this.Pl.strip && this.place === 'pineFace') c.set('#24201d');
        else c.set(['#7e7a46', '#70703e', '#8a844c'][Math.floor(k * 2.99)] as string).multiplyScalar(0.8 + lum * 0.32);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
      if (this.Cl) this.vertPatch = vp;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads') o.visible = false;
      if (o.name === 'outskirts-ground') {
        const g = (o as THREE.Mesh).geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
        if (g) {
          for (let i = 0; i < g.count; i++) {
            const lum = g.getX(i) * 0.3 + g.getY(i) * 0.59 + g.getZ(i) * 0.11;
            c.set(this.place === 'nightRock' ? '#2a2420' : '#7a7038').multiplyScalar(0.75 + lum * 0.4);
            g.setXYZ(i, c.r, c.g, c.b);
          }
          g.needsUpdate = true;
        }
      }
      // The board's own trees and rocks give way to the slope's.
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    // The great fire's black and the embers' scorch: a copy of the ground, lifted a little (the strip).
    if (this.Pl) {
      const sm = this.track(scorchMat());
      this.scorch = sm;
      const geo = this.track(terrain.mesh.geometry.clone());
      const mesh = new THREE.Mesh(geo, sm);
      mesh.position.copy(terrain.mesh.position);
      mesh.quaternion.copy(terrain.mesh.quaternion);
      mesh.scale.copy(terrain.mesh.scale);
      mesh.position.y += 0.05;
      mesh.renderOrder = 3;
      mesh.name = 'hornos-scorch';
      this.group.add(mesh);
      if (this.place === 'pineFace') (sm.uniforms.uFront as THREE.IUniform<number>).value = this.Pl.line * K;
      else (sm.uniforms.uFront as THREE.IUniform<number>).value = this.Pl.foot * K;
    }
    if (terrain.fog) terrain.fog.group.visible = true;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint(hour);
  }

  paint(hour: Hour, weather?: string): void {
    this.hour = hour;
    const look = HOURS[hour] ?? HOURS.day;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = weather === 'fog' ? look.fog[1] * 0.6 : look.fog[1];
      scene.fog.far = weather === 'fog' ? look.fog[2] * 0.55 : look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(0.5, hour === 'dusk' ? 0.08 : 0.55, 0.2);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'dive') {
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.4, 1.8, '#ff8a6e', 10, 0.8);
      } else if (e.kind === 'scare') {
        this.ctx.vfx.burst(p.setY(p.y + 1.6), { n: 16, color: '#3a3634', speed: 2.4, up: 2.0, size: 0.16, life: 14 });
      } else if (e.kind === 'kilnLit') {
        this.ctx.flare(p.setY(p.y + 1), '#ff8a3a', 2.4, 14);
        this.ctx.vfx.burst(p, { n: 18, color: '#ffb060', speed: 1.8, up: 2.6, size: 0.14, life: 16 });
      } else if (e.kind === 'crack') {
        this.ctx.shake(0.2);
        this.ctx.vfx.burst(p.setY(p.y + 0.6), { n: 22, color: '#f2f2f0', speed: 1.4, up: 2.8, size: 0.4, life: 22, smoke: true });
      } else if (e.kind === 'burst') {
        this.ctx.shake(0.8);
        this.ctx.flare(p.clone().setY(p.y + 1), '#ffb060', 6, 18);
        this.ctx.vfx.burst(p, { n: 40, color: '#4a3a2e', speed: 4, up: 4, size: 0.28, life: 20, gravity: 1 });
        this.ctx.vfx.burst(p, { n: 30, color: '#ff8a3a', speed: 3, up: 3.4, size: 0.18, life: 18 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.4, (DRUID_PATH.kilns.burstR / 1000) * S, '#ff4f3a', 10, 0.9);
      } else if (e.kind === 'cover') {
        this.ctx.vfx.burst(p, { n: 22, color: '#7a5a40', speed: 1.8, up: 1.8, size: 0.2, life: 14, gravity: 1 });
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.05), 0.3, 1.4, '#f2f4f6', 8, 0.7);
      } else if (e.kind === 'emberWarn') {
        this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.06), 0.3, (DRUID_PATH.strip.ember.r / 1000) * S, '#f2f4f6', 12, 0.8);
        this.ctx.vfx.burst(p, { n: 14, color: '#f2f2f0', speed: 0.6, up: 2.4, size: 0.4, life: 26, smoke: true });
      } else if (e.kind === 'ember') {
        this.ctx.shake(0.25);
        this.ctx.flare(p.setY(p.y + 0.6), '#ff8a3a', 2.6, 12);
        this.ctx.vfx.burst(p, { n: 26, color: '#ff9a40', speed: 2.4, up: 2.4, size: 0.14, life: 14 });
      } else if (e.kind === 'patchLit') {
        this.ctx.vfx.burst(p, { n: e.npc === 'grand' ? 10 : 16, color: '#ffb060', speed: 1.6, up: 2.4, size: 0.12, life: 14 });
      } else if (e.kind === 'patchBlack') {
        this.blacken(e.n ?? -1);
        this.ctx.vfx.burst(p, { n: 10, color: '#2a2624', speed: 0.8, up: 2, size: 0.3, life: 20, smoke: true });
      } else if (e.kind === 'scorch') {
        this.ctx.shake(0.4);
        const q = this.scorchAt[Math.min(2, Math.max(0, (e.n ?? 1) - 1))];
        if (q) this.ctx.flare(q.clone(), '#ff4f3a', 3, 16);
        if (q) this.ctx.vfx.burst(q, { n: 24, color: '#ff6a3a', speed: 2, up: 2.4, size: 0.14, life: 16 });
      } else if (e.kind === 'meet') {
        this.ctx.flare(p.setY(p.y + 1.4), '#ffd8a0', 3.4, 18);
        this.ctx.vfx.burst(p, { n: 28, color: '#3a3432', speed: 1.4, up: 4.2, size: 0.5, life: 30, smoke: true });
      } else if (e.kind === 'pineBurn') {
        this.ctx.shake(0.8);
        if (this.pineAt) this.ctx.flare(this.pineAt.clone().setY(this.pineAt.y + 4), '#ff6a2a', 8, 24);
      } else if (e.kind === 'breath') {
        this.ctx.shake(0.5);
        const [x2, y2] = e.npc.split(',').map(Number) as [number, number];
        for (let k = 0; k <= 8; k++) this.ctx.vfx.burst(this.ctx.at(e.x + ((x2 - e.x) * k) / 8, e.y + ((y2 - e.y) * k) / 8, 0.3), { n: 8, color: '#ff8a3a', speed: 1.6, up: 1.8, size: 0.16, life: 12 });
      } else if (e.kind === 'cone') {
        this.coneFlash = 1;
        if (this.coneShut) this.coneShut.visible = false;
        if (this.coneOpen) this.coneOpen.visible = true;
        if (this.coneOpen) this.ctx.vfx.burst(this.coneOpen.position.clone(), { n: 30, color: '#f2f4f6', speed: 1.6, up: 1.4, size: 0.12, life: 24 });
        this.ctx.shake(0.3);
      } else if (e.kind === 'glade') {
        this.ctx.flare(p.setY(p.y + 1.2), '#fff2e0', 4, 20);
      }
    }
  }

  draw(s: BattleState): void {
    this.lastState = s;
    if (this.Kl) this.drawKilns(s);
    if (this.Pl) this.drawStrip(s);
    if (this.Cl) this.drawCounter(s);
    this.drawFlames(s);
    this.drawRings(s);
  }

  /** The flames: on the kilns alight, along the great fire's line and in the embers' fire (the strip), over the patches alight. */
  private drawFlames(s: BattleState): void {
    const fl = this.flames;
    const w = s.senda?.summer;
    if (!fl || !w) return;
    let n = 0;
    const flame = (x: number, y: number, z: number, h: number, hot: boolean, k: number): void => {
      if (n >= fl.instanceMatrix.count) return;
      const f = 0.75 + 0.25 * Math.sin(s.tick * 1.7 + k * 3.1) + 0.15 * Math.sin(s.tick * 0.9 + k);
      this.q4.setFromEuler(this.e3.set(Math.sin(k + s.tick * 0.3) * 0.12, k, Math.cos(k * 1.3 + s.tick * 0.4) * 0.12));
      this.m4.compose(this.v3.set(x, y, z), this.q4, this.s3.set(0.9 + 0.2 * Math.sin(k), h * f, 0.9 + 0.2 * Math.cos(k)));
      fl.setMatrixAt(n, this.m4);
      fl.setColorAt(n++, this.c3.copy(hot ? HOT : FIRE));
    };
    if (this.Kl) {
      this.kilnAt.forEach((p, k) => {
        const v = w.kiln[k] as number;
        if (v === 1 || v === 2) for (let i = 0; i < 5; i++) flame(p.x + Math.cos(i * 1.3) * 0.4, p.y + 0.95, p.z + Math.sin(i * 1.3) * 0.36, (v === 2 ? 1.8 : 1.4) * (i % 2 ? 0.7 : 1), i % 2 === 1, k * 5 + i);
      });
    }
    if (this.Pl) {
      const z = w.front * K;
      for (let x = -11; x <= 11; x += 0.7) {
        const k = x * 3.3;
        flame(x + Math.sin(k) * 0.2, this.ctx.ground(x, z) + 0.05, z + Math.cos(k) * 0.25, 1.4 + 0.5 * Math.sin(k * 1.7), Math.floor(x * 3) % 2 === 0, k);
      }
      for (const e of w.embers) {
        if (s.tick < e.at || s.tick >= e.until) continue;
        const c = this.ctx.at(e.x, e.y, 0.05);
        for (let i = 0; i < 6; i++) {
          const a = i * 1.05;
          flame(c.x + Math.cos(a) * 0.6, c.y, c.z + Math.sin(a) * 0.55, 1.0, i % 2 === 0, e.x * 0.01 + i);
        }
      }
    }
    if (this.Cl && w.fire) {
      const st = w.fire;
      this.Cl.net.cells.forEach((cells, p) => {
        if (st.st[p] !== 1) return;
        const big = !!st.grand[p];
        cells.forEach((h, j) => {
          const c = this.hex(h.q, h.r);
          flame(c.x - 0.2, c.y, c.z, big ? 1.7 : 1.2, j % 2 === 0, p * 7 + j);
          flame(c.x + 0.25, c.y, c.z + 0.2, big ? 1.3 : 0.9, j % 2 === 1, p * 7 + j + 3);
        });
      });
    }
    fl.count = n;
    fl.instanceMatrix.needsUpdate = true;
    if (fl.instanceColor) fl.instanceColor.needsUpdate = true;
  }

  /** The rings: a wyvern's dive (red, closing), a kiln cracking (its burst's reach), an ember coming (white). */
  private drawRings(s: BattleState): void {
    const rings = this.rings;
    const w = s.senda?.summer;
    if (!rings || !w) return;
    let q = 0;
    const ring = (x: number, z: number, y: number, rad: number, color: THREE.ColorRepresentation): void => {
      if (q >= rings.instanceMatrix.count) return;
      this.m4.compose(this.v3.set(x, y, z), this.q4.identity(), this.s3.set(rad, 1, rad));
      rings.setMatrixAt(q, this.m4);
      rings.setColorAt(q++, this.c3.set(color));
    };
    for (const id of Object.keys(w.wy)) {
      const m = w.wy[Number(id)] as number[];
      if (m[0] !== 1) continue;
      const k = Math.max(0, Math.min(1, 1 - ((m[2] as number) - s.tick) / 36));
      const r = s.rules?.senda?.summer;
      const at = r?.kilns ? r.kilns.at[m[1] as number] : r?.counter ? r.counter.mid[m[1] as number] : undefined;
      if (!at) continue;
      const p = this.ctx.at(at.x, at.y, 0.08);
      ring(p.x, p.z, p.y, 1.6, '#ff4f3a');
      ring(p.x, p.z, p.y, 1.6 * (1 - k * 0.8), '#ff8a6e');
    }
    if (this.Kl) {
      const R = (DRUID_PATH.kilns.burstR / 1000) * S;
      this.kilnAt.forEach((p, k) => {
        if (w.kiln[k] !== 2) return;
        const left = Math.max(0, Math.min(1, ((w.kt[k] as number) - s.tick) / Math.max(1, DRUID_PATH.kilns.warn)));
        ring(p.x, p.z, p.y + 0.07, R, '#ff4f3a');
        ring(p.x, p.z, p.y + 0.07, R * (0.15 + left * 0.85), '#ff8a6e');
      });
    }
    if (this.Pl) {
      for (const e of w.embers) {
        if (s.tick >= e.at) continue;
        const p = this.ctx.at(e.x, e.y, 0.08);
        const k = Math.max(0, Math.min(1, (e.at - s.tick) / Math.max(1, DRUID_PATH.strip.ember.warn)));
        ring(p.x, p.z, p.y, (DRUID_PATH.strip.ember.r / 1000) * S, '#f2f4f6');
        ring(p.x, p.z, p.y, (DRUID_PATH.strip.ember.r / 1000) * S * (0.15 + k * 0.85), '#ff8a6e');
      }
    }
    rings.count = q;
    rings.instanceMatrix.needsUpdate = true;
    if (rings.instanceColor) rings.instanceColor.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (this.ash && camera) this.ash.frame(now, lookPoint(camera, this.look));
    this.coneFlash = Math.max(0, this.coneFlash - dt * 0.4);
    // The wind's lean, eased; its arrows turn with it (swinging while the smoke bends).
    this.lean.lerp(this.leanTo, Math.min(1, dt * 1.5));
    const st0 = this.lastState?.senda?.summer;
    const r0 = this.lastState?.rules?.senda?.summer?.counter;
    const swing = st0 && r0 && st0.t < r0.flip && st0.t >= r0.flip - r0.warn ? Math.sin(now * 7) * 0.5 : 0;
    for (const a of this.arrows) {
      const want = this.arrowTurn + swing;
      a.rotation.y += (want - a.rotation.y) * Math.min(1, dt * 4);
    }
    if (this.smokeMatU) {
      (this.smokeMatU.uniforms.uTime as THREE.IUniform<number>).value = now;
      (this.smokeMatU.uniforms.uLean as THREE.IUniform<THREE.Vector2>).value.copy(this.lean);
    }
    const s = this.lastState;
    if (this.scorch && s) {
      const u = this.scorch.uniforms;
      (u.uTime as THREE.IUniform<number>).value = now;
      const w = s.senda?.summer;
      if (w && this.place !== 'pineFace') (u.uFront as THREE.IUniform<number>).value = w.front * K;
      // The embers that fell: their scorch stays.
      const spots = (u.uSpots as THREE.IUniform<THREE.Vector3[]>).value;
      if (w) {
        const fell = w.embers.filter((e) => s.tick >= e.at);
        for (let i = 0; i < spots.length; i++) {
          const e = fell[fell.length - 1 - i];
          if (e) (spots[i] as THREE.Vector3).set(e.x * K, e.y * K, (DRUID_PATH.strip.ember.r / 1000) * S * 0.9);
        }
      }
    }
    this.glow.begin();
    for (const l of this.lights) {
      const f = l.flick ? 0.82 + 0.18 * Math.sin(now * (3 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    const w = s?.senda?.summer;
    // The kilns alight glow orange; cracking, red and pulsing.
    if (this.Kl && w) {
      this.kilnAt.forEach((p, k) => {
        const v = w.kiln[k] as number;
        if (v === 1) this.glow.add(p.x, p.y + 1.0, p.z, FIRE, 2.2, 0.7);
        else if (v === 2) this.glow.add(p.x, p.y + 0.6, p.z, RED, 2.8 + Math.sin(now * 10) * 0.5, 0.85);
      });
    }
    // The great fire's line glows; the embers' fires.
    if (this.Pl && w) {
      const z = w.front * K;
      for (let x = -10; x <= 10; x += 2.5) this.glow.add(x, this.ctx.ground(x, z) + 0.8, z, FIRE, 3.2, 0.65 + 0.15 * Math.sin(now * 5 + x));
      if (s) for (const e of w.embers) if (s.tick >= e.at && s.tick < e.until) this.glow.add(e.x * K, 0.8, e.y * K, FIRE, 2.4, 0.6);
    }
    // The patches alight; the pine's heat above and below once it has it; the scorches on its bark.
    if (this.Cl && w?.fire) {
      const st = w.fire;
      st.st.forEach((v, p) => {
        if (v !== 1) return;
        const m = this.Cl?.mid[p];
        if (m) this.glow.add(m.x * K, 0.9, m.y * K, st.grand[p] ? HOT : FIRE, 3.4, 0.6 + 0.15 * Math.sin(now * 6 + p));
      });
      for (let i = 0; i < Math.min(3, st.marks); i++) {
        const q = this.scorchAt[i];
        if (q) this.glow.add(q.x, q.y, q.z, RED, 1.2, 0.7);
      }
    }
    if (this.pineAt && this.coneFlash > 0 && this.coneOpen) {
      const c = this.coneOpen.position;
      this.glow.add(c.x, c.y, c.z, PALE, 3 + this.coneFlash * 6, this.coneFlash);
    }
    this.glow.end();
    void this.hour;
  }

  /** The patches' middles in world points (the HUD's marks hang over them). */
  patchPoints(): THREE.Vector3[] {
    return (this.Cl?.mid ?? []).map((m) => this.ctx.at(m.x, m.y, 0.6));
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.ash?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
